/**
 * Lesson times are UK times.
 *
 * The backend stores an occurrence as a plain date and a plain time — "2026-08-30",
 * "14:00" — with no offset, and reads them back in `subscriptions.lesson_timezone`,
 * which is Europe/London. The browser does not know that. `new Date('2026-08-30T14:00')`
 * is parsed in whatever zone the machine happens to sit in, so a tutor working from
 * Sofia was told a 14:00 UK lesson began at 14:00 EEST and the countdown ran two
 * hours fast — far enough out that the modal declared rescheduling closed while
 * there were still three hours to go.
 *
 * Everything user-facing about when a lesson happens should go through here.
 */
const LESSON_TIMEZONE = 'Europe/London'

/**
 * How far the given instant's wall clock in `timeZone` sits from UTC, in ms.
 */
function zoneOffsetMs(instant, timeZone) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  )

  const asIfUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  )

  return asIfUtc - instant.getTime()
}

/**
 * Turn a UK date and time into the instant it actually refers to.
 *
 * @param {string|null|undefined} date "YYYY-MM-DD"
 * @param {string|null|undefined} time "HH:mm" or "HH:mm:ss"
 * @returns {Date|null} null when either part is missing or unparseable
 */
export function lessonStart(date, time) {
  if (!date || !time) {
    return null
  }

  const [year, month, day] = String(date).split('-').map(Number)
  const [hour, minute] = String(time).split(':').map(Number)

  if ([year, month, day, hour, minute].some((part) => !Number.isFinite(part))) {
    return null
  }

  const naive = Date.UTC(year, month - 1, day, hour, minute)

  // The offset depends on the instant, and the instant is what we are solving
  // for, so guess with the offset at the naive time and correct once. The second
  // pass is what gets the hour either side of a DST change right.
  const firstPass = naive - zoneOffsetMs(new Date(naive), LESSON_TIMEZONE)
  const corrected = naive - zoneOffsetMs(new Date(firstPass), LESSON_TIMEZONE)

  return new Date(corrected)
}

/**
 * Whole minutes from now until a UK lesson time. Negative once it has started.
 *
 * @returns {number|null} null when the lesson time cannot be read
 */
export function minutesUntilLesson(date, time) {
  const start = lessonStart(date, time)

  if (!start) {
    return null
  }

  return Math.floor((start.getTime() - Date.now()) / 60000)
}

export { LESSON_TIMEZONE }
